import React, { useState } from 'react';
import { View, Text, StyleSheet, ScrollView, TouchableOpacity, TextInput } from 'react-native';
import { useNavigation } from '@react-navigation/native';
import { StackNavigationProp } from '@react-navigation/stack';
import { Icon, IconName } from '../components/Icon';
import { colors, spacing, fontWeights, borderRadius } from '../theme';
import type { RootStackParamList } from '../types/navigation';

type NavigationProp = StackNavigationProp<RootStackParamList>;

interface FinancialGoal {
  id: string;
  title: string;
  desc: string;
  icon: IconName;
  recommendedRatio: string;
}

const GOALS: FinancialGoal[] = [
  {
    id: 'income_protection',
    title: 'Bảo vệ thu nhập gia đình',
    desc: 'Đảm bảo nguồn tài chính cho gia đình trong 10-15 năm nếu có rủi ro người trụ cột',
    icon: 'shield',
    recommendedRatio: 'STBH = 10 năm chi phí sinh hoạt',
  },
  {
    id: 'education',
    title: 'Quỹ học vấn cho con',
    desc: 'Chuẩn bị nguồn tài chính cho con vào đại học hoặc du học',
    icon: 'school',
    recommendedRatio: 'Quỹ mục tiêu: 500Tr - 1.5 Tỷ VNĐ',
  },
  {
    id: 'retirement',
    title: 'Quỹ hưu trí an nhàn',
    desc: 'Đảm bảo thu nhập độc lập khi về hưu không phụ thuộc con cái',
    icon: 'elderly',
    recommendedRatio: 'Tích lũy 70% chi phí hiện tại',
  },
  {
    id: 'investment',
    title: 'Tích lũy & Đầu tư',
    desc: 'Tăng trưởng tài sản an toàn kết hợp bảo vệ quyền lợi trọn đời',
    icon: 'trending_up',
    recommendedRatio: '10-15% thu nhập hàng năm',
  },
];

export const M17FinancialSurveyScreen: React.FC = () => {
  const navigation = useNavigation<NavigationProp>();

  // State
  const [step, setStep] = useState<1 | 2 | 3>(1);
  const [selectedGoal, setSelectedGoal] = useState<string>('income_protection');

  // Input states
  const [age, setAge] = useState<string>('30');
  const [gender, setGender] = useState<'Nam' | 'Nữ'>('Nam');
  const [monthlyIncome, setMonthlyIncome] = useState<string>('30'); // Triệu VNĐ / tháng
  const [monthlyExpense, setMonthlyExpense] = useState<string>('15'); // Triệu VNĐ / tháng
  const [protectionYears, setProtectionYears] = useState<string>('10'); // Số năm bảo vệ
  const [dependents, setDependents] = useState<string>('2'); // Số người phụ thuộc

  // Formatted numbers for display
  const incomeNum = (parseFloat(monthlyIncome) || 0) * 1_000_000;
  const expenseNum = (parseFloat(monthlyExpense) || 0) * 1_000_000;
  const yearsNum = parseInt(protectionYears, 10) || 10;

  // Calculation formulas
  // 1. Recommended Sum Assured (STBH gợi ý) = 12 * Chi phí tháng * Số năm bảo vệ
  const recommendedSumAssured = Math.max(500_000_000, Math.round((expenseNum * 12 * yearsNum) / 50_000_000) * 50_000_000);
  
  // 2. Recommended Annual Premium (Phí gợi ý/năm) = ~10% đến 12% Thu nhập năm
  const annualIncome = incomeNum * 12;
  const recommendedEstPremium = Math.max(12_000_000, Math.round((annualIncome * 0.12) / 1_000_000) * 1_000_000);

  const formatCurrency = (val: number) => {
    if (val >= 1_000_000_000) {
      return `${(val / 1_000_000_000).toFixed(1)} Tỷ VNĐ`;
    }
    return `${(val / 1_000_000).toFixed(0)} Triệu VNĐ`;
  };

  const handleCreateProposal = () => {
    navigation.navigate('M06InputCustomerScreen', {
      customerAge: parseInt(age, 10) || 30,
      customerGender: gender,
    });
  };

  return (
    <ScrollView style={styles.container} contentContainerStyle={styles.contentContainer}>
      {/* Header */}
      <View style={styles.headerCard}>
        <View style={styles.badge}>
          <Icon name="psychology" size={16} color={colors.primary} />
          <Text style={styles.badgeText}>FINANCIAL NEED ANALYSIS (FNA)</Text>
        </View>
        <Text style={styles.headerTitle}>Khảo Sát Nhu Cầu Tài Chính</Text>
        <Text style={styles.headerSubtitle}>
          Phân tích khoảng trống tài chính & xác định Mệnh giá Bảo vệ tối ưu cho khách hàng
        </Text>

        {/* Stepper Bar */}
        <View style={styles.stepperContainer}>
          <TouchableOpacity
            style={[styles.stepItem, step === 1 && styles.stepActive]}
            onPress={() => setStep(1)}
          >
            <Text style={[styles.stepNumber, step === 1 && styles.stepNumberActive]}>1</Text>
            <Text style={[styles.stepLabel, step === 1 && styles.stepLabelActive]}>Mục tiêu</Text>
          </TouchableOpacity>

          <View style={styles.stepDivider} />

          <TouchableOpacity
            style={[styles.stepItem, step === 2 && styles.stepActive]}
            onPress={() => setStep(2)}
          >
            <Text style={[styles.stepNumber, step === 2 && styles.stepNumberActive]}>2</Text>
            <Text style={[styles.stepLabel, step === 2 && styles.stepLabelActive]}>Tài chính</Text>
          </TouchableOpacity>

          <View style={styles.stepDivider} />

          <TouchableOpacity
            style={[styles.stepItem, step === 3 && styles.stepActive]}
            onPress={() => setStep(3)}
          >
            <Text style={[styles.stepNumber, step === 3 && styles.stepNumberActive]}>3</Text>
            <Text style={[styles.stepLabel, step === 3 && styles.stepLabelActive]}>Kết quả</Text>
          </TouchableOpacity>
        </View>
      </View>

      {/* STEP 1: Select Goal */}
      {step === 1 && (
        <View style={styles.stepSection}>
          <Text style={styles.sectionTitle}>Bước 1: Chọn mục tiêu tài chính ưu tiên</Text>
          <Text style={styles.sectionDesc}>Mỗi mục tiêu có công thức phân tích nhu cầu STBH khác nhau.</Text>

          <View style={styles.goalsGrid}>
            {GOALS.map((goal) => {
              const isSelected = selectedGoal === goal.id;
              return (
                <TouchableOpacity
                  key={goal.id}
                  style={[styles.goalCard, isSelected && styles.goalCardSelected]}
                  onPress={() => setSelectedGoal(goal.id)}
                >
                  <View style={[styles.goalIconWrap, isSelected && styles.goalIconWrapSelected]}>
                    <Icon name={goal.icon} size={28} color={isSelected ? colors.white : colors.primary} />
                  </View>
                  <Text style={[styles.goalTitle, isSelected && styles.goalTitleSelected]}>{goal.title}</Text>
                  <Text style={styles.goalDesc}>{goal.desc}</Text>

                  <View style={styles.ratioBadge}>
                    <Text style={styles.ratioBadgeText}>💡 {goal.recommendedRatio}</Text>
                  </View>
                </TouchableOpacity>
              );
            })}
          </View>

          <TouchableOpacity style={styles.primaryButton} onPress={() => setStep(2)}>
            <Text style={styles.primaryButtonText}>Tiếp tục: Nhập thông số tài chính</Text>
            <Icon name="arrow_forward" size={20} color={colors.white} />
          </TouchableOpacity>
        </View>
      )}

      {/* STEP 2: Input Financial Specs */}
      {step === 2 && (
        <View style={styles.stepSection}>
          <Text style={styles.sectionTitle}>Bước 2: Nhập thông tin khảo sát khách hàng</Text>
          <Text style={styles.sectionDesc}>Vui lòng nhập các thông số thu nhập & chi phí hiện tại.</Text>

          <View style={styles.formCard}>
            {/* Demographics */}
            <View style={styles.formRow}>
              <View style={styles.formFieldHalf}>
                <Text style={styles.label}>Tuổi khách hàng</Text>
                <TextInput
                  style={styles.input}
                  keyboardType="numeric"
                  value={age}
                  onChangeText={setAge}
                  placeholder="Tuổi (0-70)"
                />
              </View>

              <View style={styles.formFieldHalf}>
                <Text style={styles.label}>Giới tính</Text>
                <View style={styles.genderRow}>
                  <TouchableOpacity
                    style={[styles.genderBtn, gender === 'Nam' && styles.genderBtnActive]}
                    onPress={() => setGender('Nam')}
                  >
                    <Text style={[styles.genderBtnText, gender === 'Nam' && styles.genderBtnTextActive]}>Nam</Text>
                  </TouchableOpacity>
                  <TouchableOpacity
                    style={[styles.genderBtn, gender === 'Nữ' && styles.genderBtnActive]}
                    onPress={() => setGender('Nữ')}
                  >
                    <Text style={[styles.genderBtnText, gender === 'Nữ' && styles.genderBtnTextActive]}>Nữ</Text>
                  </TouchableOpacity>
                </View>
              </View>
            </View>

            {/* Financial Numbers */}
            <View style={styles.formField}>
              <Text style={styles.label}>Thu nhập hàng tháng của gia đình (Triệu VNĐ)</Text>
              <TextInput
                style={styles.input}
                keyboardType="numeric"
                value={monthlyIncome}
                onChangeText={setMonthlyIncome}
                placeholder="VD: 30"
              />
            </View>

            <View style={styles.formField}>
              <Text style={styles.label}>Chi phí sinh hoạt hàng tháng (Triệu VNĐ)</Text>
              <TextInput
                style={styles.input}
                keyboardType="numeric"
                value={monthlyExpense}
                onChangeText={setMonthlyExpense}
                placeholder="VD: 15"
              />
            </View>

            <View style={styles.formRow}>
              <View style={styles.formFieldHalf}>
                <Text style={styles.label}>Số năm bảo vệ</Text>
                <TextInput
                  style={styles.input}
                  keyboardType="numeric"
                  value={protectionYears}
                  onChangeText={setProtectionYears}
                  placeholder="10"
                />
              </View>

              <View style={styles.formFieldHalf}>
                <Text style={styles.label}>Số người phụ thuộc</Text>
                <TextInput
                  style={styles.input}
                  keyboardType="numeric"
                  value={dependents}
                  onChangeText={setDependents}
                  placeholder="2"
                />
              </View>
            </View>
          </View>

          <View style={styles.btnRow}>
            <TouchableOpacity style={styles.secondaryButton} onPress={() => setStep(1)}>
              <Icon name="arrow_back" size={20} color={colors.onSurface} />
              <Text style={styles.secondaryButtonText}>Quay lại</Text>
            </TouchableOpacity>

            <TouchableOpacity style={[styles.primaryButton, { flex: 1, marginLeft: spacing[3] }]} onPress={() => setStep(3)}>
              <Text style={styles.primaryButtonText}>Xem kết quả khảo sát</Text>
              <Icon name="analytics" size={20} color={colors.white} />
            </TouchableOpacity>
          </View>
        </View>
      )}

      {/* STEP 3: Analysis Results */}
      {step === 3 && (
        <View style={styles.stepSection}>
          <Text style={styles.sectionTitle}>Bước 3: Kết quả phân tích nhu cầu FNA</Text>
          <Text style={styles.sectionDesc}>Gói giải pháp được tính toán dựa trên các chỉ số rủi ro & chi phí.</Text>

          {/* Results Summary Card */}
          <View style={styles.resultCard}>
            <View style={styles.resultHeader}>
              <Icon name="verified" size={32} color={colors.primary} />
              <View style={{ marginLeft: spacing[3], flex: 1 }}>
                <Text style={styles.resultHeaderTitle}>Đề Xuất Gói Bảo Vệ Tối Ưu</Text>
                <Text style={styles.resultHeaderSub}>Manulife Xanh Ước Mơ + 5 Sản phẩm bổ trợ</Text>
              </View>
            </View>

            <View style={styles.resultDivider} />

            <View style={styles.metricGrid}>
              <View style={styles.metricItem}>
                <Text style={styles.metricLabel}>Số tiền bảo hiểm (STBH) gợi ý</Text>
                <Text style={styles.metricValuePrimary}>{formatCurrency(recommendedSumAssured)}</Text>
                <Text style={styles.metricSub}>Bảo vệ đủ 100% chi phí 10 năm</Text>
              </View>

              <View style={styles.metricItem}>
                <Text style={styles.metricLabel}>Phí đóng dự kiến / năm</Text>
                <Text style={styles.metricValueSecondary}>{formatCurrency(recommendedEstPremium)} / năm</Text>
                <Text style={styles.metricSub}>~12% tổng thu nhập hàng năm</Text>
              </View>
            </View>

            <View style={styles.resultDivider} />

            {/* Analysis details */}
            <Text style={styles.analysisTitle}>💡 Đánh giá an toàn tài chính:</Text>
            <View style={styles.analysisItem}>
              <Icon name="check_circle" size={18} color={colors.primary} />
              <Text style={styles.analysisText}>
                STBH {formatCurrency(recommendedSumAssured)} đảm bảo cho {dependents} người phụ thuộc duy trì mức sống trong {protectionYears} năm.
              </Text>
            </View>
            <View style={styles.analysisItem}>
              <Icon name="check_circle" size={18} color={colors.primary} />
              <Text style={styles.analysisText}>
                Phí bảo hiểm {formatCurrency(recommendedEstPremium)}/năm nằm trong vùng an toàn (dưới 15% thu nhập) không gây gánh nặng tài chính.
              </Text>
            </View>
          </View>

          {/* Actions */}
          <View style={styles.btnRow}>
            <TouchableOpacity style={styles.secondaryButton} onPress={() => setStep(2)}>
              <Icon name="edit" size={20} color={colors.onSurface} />
              <Text style={styles.secondaryButtonText}>Chỉnh sửa số liệu</Text>
            </TouchableOpacity>

            <TouchableOpacity style={[styles.primaryButton, { flex: 1, marginLeft: spacing[3] }]} onPress={handleCreateProposal}>
              <Text style={styles.primaryButtonText}>Tạo minh họa ngay</Text>
              <Icon name="play_arrow" size={20} color={colors.white} />
            </TouchableOpacity>
          </View>
        </View>
      )}
    </ScrollView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: colors.background,
  },
  contentContainer: {
    padding: spacing[4],
    paddingBottom: spacing[8],
  },
  headerCard: {
    backgroundColor: colors.surface,
    borderRadius: borderRadius.lg,
    padding: spacing[5],
    marginBottom: spacing[4],
    borderWidth: 1,
    borderColor: colors.outlineVariant,
  },
  badge: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: colors.primary + '15',
    alignSelf: 'flex-start',
    paddingHorizontal: spacing[3],
    paddingVertical: spacing[1],
    borderRadius: borderRadius.full,
    marginBottom: spacing[2],
  },
  badgeText: {
    fontSize: 11,
    fontWeight: fontWeights.bold,
    color: colors.primary,
    marginLeft: spacing[1],
    letterSpacing: 0.5,
  },
  headerTitle: {
    fontSize: 22,
    fontWeight: fontWeights.bold,
    color: colors.onSurface,
    marginBottom: spacing[1],
  },
  headerSubtitle: {
    fontSize: 14,
    color: colors.onSurfaceVariant,
    lineHeight: 20,
    marginBottom: spacing[4],
  },
  stepperContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    backgroundColor: colors.background,
    borderRadius: borderRadius.md,
    padding: spacing[2],
  },
  stepItem: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: spacing[2],
    paddingVertical: spacing[1],
    borderRadius: borderRadius.sm,
  },
  stepActive: {
    backgroundColor: colors.primary + '20',
  },
  stepNumber: {
    width: 24,
    height: 24,
    borderRadius: 12,
    backgroundColor: colors.outlineVariant,
    color: colors.onSurfaceVariant,
    textAlign: 'center',
    lineHeight: 24,
    fontSize: 12,
    fontWeight: fontWeights.bold,
    marginRight: spacing[1],
  },
  stepNumberActive: {
    backgroundColor: colors.primary,
    color: colors.white,
  },
  stepLabel: {
    fontSize: 13,
    color: colors.onSurfaceVariant,
    fontWeight: fontWeights.medium,
  },
  stepLabelActive: {
    color: colors.primary,
    fontWeight: fontWeights.bold,
  },
  stepDivider: {
    flex: 1,
    height: 1,
    backgroundColor: colors.outlineVariant,
    marginHorizontal: spacing[1],
  },
  stepSection: {
    marginTop: spacing[2],
  },
  sectionTitle: {
    fontSize: 18,
    fontWeight: fontWeights.bold,
    color: colors.onSurface,
    marginBottom: spacing[1],
  },
  sectionDesc: {
    fontSize: 14,
    color: colors.onSurfaceVariant,
    marginBottom: spacing[4],
  },
  goalsGrid: {
    gap: spacing[3],
    marginBottom: spacing[5],
  },
  goalCard: {
    backgroundColor: colors.surface,
    borderRadius: borderRadius.md,
    padding: spacing[4],
    borderWidth: 1,
    borderColor: colors.outlineVariant,
  },
  goalCardSelected: {
    borderColor: colors.primary,
    backgroundColor: colors.primary + '08',
    borderWidth: 2,
  },
  goalIconWrap: {
    width: 48,
    height: 48,
    borderRadius: 24,
    backgroundColor: colors.primary + '15',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: spacing[3],
  },
  goalIconWrapSelected: {
    backgroundColor: colors.primary,
  },
  goalTitle: {
    fontSize: 16,
    fontWeight: fontWeights.bold,
    color: colors.onSurface,
    marginBottom: spacing[1],
  },
  goalTitleSelected: {
    color: colors.primary,
  },
  goalDesc: {
    fontSize: 13,
    color: colors.onSurfaceVariant,
    lineHeight: 18,
    marginBottom: spacing[3],
  },
  ratioBadge: {
    backgroundColor: colors.surfaceVariant,
    paddingHorizontal: spacing[3],
    paddingVertical: spacing[1] + 2,
    borderRadius: borderRadius.sm,
    alignSelf: 'flex-start',
  },
  ratioBadgeText: {
    fontSize: 12,
    color: colors.onSurfaceVariant,
    fontWeight: fontWeights.medium,
  },
  formCard: {
    backgroundColor: colors.surface,
    borderRadius: borderRadius.lg,
    padding: spacing[4],
    marginBottom: spacing[4],
    borderWidth: 1,
    borderColor: colors.outlineVariant,
  },
  formRow: {
    flexDirection: 'row',
    gap: spacing[3],
    marginBottom: spacing[3],
  },
  formFieldHalf: {
    flex: 1,
  },
  formField: {
    marginBottom: spacing[3],
  },
  label: {
    fontSize: 13,
    fontWeight: fontWeights.bold,
    color: colors.onSurface,
    marginBottom: spacing[1],
  },
  input: {
    backgroundColor: colors.background,
    borderWidth: 1,
    borderColor: colors.outlineVariant,
    borderRadius: borderRadius.md,
    paddingHorizontal: spacing[3],
    paddingVertical: spacing[2] + 2,
    fontSize: 14,
    color: colors.onSurface,
  },
  genderRow: {
    flexDirection: 'row',
    gap: spacing[2],
  },
  genderBtn: {
    flex: 1,
    paddingVertical: spacing[2] + 2,
    backgroundColor: colors.background,
    borderRadius: borderRadius.md,
    borderWidth: 1,
    borderColor: colors.outlineVariant,
    alignItems: 'center',
  },
  genderBtnActive: {
    backgroundColor: colors.primary,
    borderColor: colors.primary,
  },
  genderBtnText: {
    fontSize: 14,
    fontWeight: fontWeights.bold,
    color: colors.onSurface,
  },
  genderBtnTextActive: {
    color: colors.white,
  },
  primaryButton: {
    backgroundColor: colors.primary,
    borderRadius: borderRadius.md,
    paddingVertical: spacing[3] + 2,
    paddingHorizontal: spacing[4],
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: spacing[2],
  },
  primaryButtonText: {
    color: colors.white,
    fontSize: 15,
    fontWeight: fontWeights.bold,
  },
  secondaryButton: {
    backgroundColor: colors.surface,
    borderWidth: 1,
    borderColor: colors.outlineVariant,
    borderRadius: borderRadius.md,
    paddingVertical: spacing[3] + 2,
    paddingHorizontal: spacing[4],
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: spacing[2],
  },
  secondaryButtonText: {
    color: colors.onSurface,
    fontSize: 14,
    fontWeight: fontWeights.bold,
  },
  btnRow: {
    flexDirection: 'row',
    marginTop: spacing[2],
  },
  resultCard: {
    backgroundColor: colors.surface,
    borderRadius: borderRadius.lg,
    padding: spacing[5],
    marginBottom: spacing[5],
    borderWidth: 1,
    borderColor: colors.primary + '30',
  },
  resultHeader: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  resultHeaderTitle: {
    fontSize: 18,
    fontWeight: fontWeights.bold,
    color: colors.onSurface,
  },
  resultHeaderSub: {
    fontSize: 13,
    color: colors.onSurfaceVariant,
  },
  resultDivider: {
    height: 1,
    backgroundColor: colors.outlineVariant,
    marginVertical: spacing[4],
  },
  metricGrid: {
    gap: spacing[4],
  },
  metricItem: {
    backgroundColor: colors.background,
    padding: spacing[3] + 2,
    borderRadius: borderRadius.md,
  },
  metricLabel: {
    fontSize: 12,
    color: colors.onSurfaceVariant,
    textTransform: 'uppercase',
    letterSpacing: 0.5,
    fontWeight: fontWeights.bold,
    marginBottom: spacing[1],
  },
  metricValuePrimary: {
    fontSize: 24,
    fontWeight: fontWeights.bold,
    color: colors.primary,
    marginBottom: spacing[1],
  },
  metricValueSecondary: {
    fontSize: 20,
    fontWeight: fontWeights.bold,
    color: colors.tertiary || colors.primary,
    marginBottom: spacing[1],
  },
  metricSub: {
    fontSize: 12,
    color: colors.onSurfaceVariant,
  },
  analysisTitle: {
    fontSize: 14,
    fontWeight: fontWeights.bold,
    color: colors.onSurface,
    marginBottom: spacing[2],
  },
  analysisItem: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: spacing[2],
    marginBottom: spacing[2],
  },
  analysisText: {
    flex: 1,
    fontSize: 13,
    color: colors.onSurfaceVariant,
    lineHeight: 18,
  },
});

